import React, { useContext, useEffect } from "react";

import { FormProvider, useForm } from "react-hook-form";

import { Button, FormButton } from "@components/buttons";
import SearchInputField from "@components/inputs/shared/SearchInputField";
import { useRootAppStore } from "@hooks";
import { MainHtmlElementContext } from "@state/context/main-html/MainHtmlElementContext";
import { SearchAction } from "@state/stores/offer/search/SearchStore";
import { MobilePlatform } from "@state/stores/types";
import { isCapacitorPlatform } from "@utils/specific/capacitor/helpers";
import useSafeAreaListener from "@utils/specific/capacitor/useSafeAreaListener";
import { HEADER_HEIGHT } from "@utils/style/constants";
import { observer } from "mobx-react";

import HeaderSuggestedSearchPopup from "./HeaderSuggestedSearchPopup";

export default observer(function HeaderSearch() {
	const mainRef = useContext(MainHtmlElementContext);

	const rootStore = useRootAppStore<MobilePlatform>();
	const searchStore = rootStore.searchStore;

	const methods = useForm({
		mode: "onChange",
		defaultValues: {
			search: searchStore.searchTerm,
		},
	});

	const { top } = useSafeAreaListener();

	const onChange: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
		const code = e.which;
		if (code === 13) {
			if (searchStore.searchValidation.test(searchStore.searchTerm)) {
				searchStore.setIsSuggestedListOpen(false);
				const searchResult = searchStore.onSubmit({
					action: SearchAction.SEARCH,
					period: "full",
				});

				if (searchResult) {
					methods.reset();
				}

				searchStore.onReset();
			}
		} else if (code === 27) {
			searchStore.resetSearch();
		}
	};

	const handleSearchClose = () => {
		searchStore.setIsSearchOpen(false);
	};

	const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		searchStore.onTermChange(e.target.value);
		if (
			e.target.value.length >= searchStore.minLengthForFilterSuggestedList
		) {
			searchStore.setIsSuggestedListOpen(true);
		} else {
			searchStore.setIsSuggestedListOpen(false);
		}
	};

	useEffect(() => {
		const fetchSuggestedList = async () => {
			await searchStore.getSuggestedSearchList();
		};

		if (searchStore.isSearchOpen) {
			fetchSuggestedList();

			if (mainRef) {
				mainRef.style.overflow = "hidden";
			}
		} else {
			if (mainRef) {
				mainRef.style.overflow = "";
			}
		}
	}, [searchStore.isSearchOpen]);

	if (!searchStore.isSearchOpen) {
		return null;
	}

	const shouldDisplayPopup =
		searchStore.filteredSearchList?.length ||
		methods.getValues("search")?.length > 2 ||
		methods.formState.errors.search != null;

	const searchOverlayPadding = isCapacitorPlatform()
		? { top: top + HEADER_HEIGHT }
		: {};

	return (
		<div
			className={
				"header__main header--root__main" +
				(searchStore.isSearchOpen ? " u-padd--left--reset" : "")
			}
		>
			<FormProvider {...methods}>
				<Button
					onClick={handleSearchClose}
					atClassName="headerBtn--Search-Close"
					className="header--page__back btn btn--square btn--ghost"
					iconOnly
					iconClassName="u-icon--tny u-icon--caret-left u-color--core--snow"
				/>

				<div className="header__content header__searchbar">
					<SearchInputField
						name="search"
						validate={(value: string | undefined) => {
							if (value == null) {
								return "";
							}
							if (value.trim().length < 3) {
								return "SEARCH.INFO";
							}

							return (
								searchStore.searchValidation.test(value) ||
								"SEARCH.INFO"
							);
						}}
						onKeyUp={onChange}
						onChange={handleOnChange}
						onClick={() => {
							searchStore.setIsSuggestedListOpen(true);
						}}
						defaultValue={methods.getValues("search") || ""}
						placeholder="HEADER.SEARCH_PLACEHOLDER"
						autoComplete="off"
						autoFocus
						inputClassName="at-searchModalInput header__searchbar__input input input--med"
						customErrorClassName="searchbar__validation message--validation field-validation-error"
					/>
					{searchStore.isSearchOpen && (
						<div
							onClick={() => {
								handleSearchClose();
							}}
							style={searchOverlayPadding}
							className="header__searchbar__overlay"
						></div>
					)}
				</div>

				<div className="header__actions">
					<FormButton
						onClick={(e) => {
							e.stopPropagation();
							searchStore.setIsSuggestedListOpen(false);

							const searchResult = searchStore.onSubmit({
								action: SearchAction.SEARCH,
								period: "full",
							});

							if (searchResult) {
								methods.reset();
							}
						}}
						atClassName="headerBtn--Search-Submit"
						className="btn--square btn--square--sml btn--ghost"
						iconOnly
						iconClassName="u-icon--sml u-icon--search--reg u-color--core--snow"
					/>
				</div>

				{shouldDisplayPopup && <HeaderSuggestedSearchPopup />}
			</FormProvider>
		</div>
	);
});
